/**
 * Mock Permission Requests Data
 * Sample permission requests for Andes tables
 * @module data/starnexus/mockPermissionRequests
 */

import { PermissionRequest } from '../../types/starnexus/andesAccess';

/**
 * Sample permission requests with various statuses and identity types
 */
export const mockPermissionRequests: PermissionRequest[] = [
  // Approved requests
  {
    requestId: 'pr-001',
    datasetId: 'ds-001',
    datasetName: 'bronze_rpt_cds_retail_cga',
    providerId: '75d99108-5fc6-449b-a9f9-d0716f2bbc29',
    providerName: 'starcatcher_origin_prod',
    tableName: 'bronze_rpt_cds_retail_cga',
    tableVersion: 1,
    identityType: 'CRADLE_ACCOUNT',
    identityValue: 'aurora-weaver-cradle-prod',
    businessJustificationType: 'Provide Amazon services for repeatable interactions',
    businessJustification:
      'Access to the AuroraWeaver Cradle Infrastructure, managed by the AGL CN BI Team, is essential for optimizing system performance and ensuring smooth data flow across critical pipelines.',
    status: 'approved',
    requestedBy: 'yyangzam',
    requestedAt: new Date('2025-01-15T10:30:00Z'),
    reviewedBy: 'admin',
    reviewedAt: new Date('2025-01-16T14:20:00Z'),
    reviewNotes: 'Approved for production access'
  },
  {
    requestId: 'pr-002',
    datasetId: 'ds-001',
    datasetName: 'bronze_rpt_cds_retail_cga',
    providerId: '75d99108-5fc6-449b-a9f9-d0716f2bbc29',
    providerName: 'starcatcher_origin_prod',
    tableName: 'bronze_rpt_cds_retail_cga',
    tableVersion: 1,
    identityType: 'DATANET_DBUSER',
    identityValue: 'delta_site_tier_2_prod',
    businessJustificationType: 'Provide Amazon services for repeatable interactions',
    businessJustification: 'Required for Delta Site production analytics and reporting dashboards.',
    status: 'approved',
    requestedBy: 'yyangzam',
    requestedAt: new Date('2025-01-15T10:35:00Z'),
    reviewedBy: 'admin',
    reviewedAt: new Date('2025-01-16T14:25:00Z')
  },
  {
    requestId: 'pr-003',
    datasetId: 'ds-002',
    datasetName: 'bronze_rpt_cds_carrier_perf',
    providerId: '75d99108-5fc6-449b-a9f9-d0716f2bbc29',
    providerName: 'starcatcher_origin_prod',
    tableName: 'bronze_rpt_cds_carrier_perf',
    tableVersion: 1,
    identityType: 'IAM_ROLE',
    identityValue: 'arn:aws:iam::058264547985:role/QuickSight-us-east-1-delta-site-prod-andes_bi-DO_NOT_DELETE',
    businessJustificationType: 'Provide Amazon services for repeatable interactions',
    businessJustification: 'QuickSight dashboards require access to shipment data for L8+ business reviews.',
    status: 'approved',
    requestedBy: 'yyangzam',
    requestedAt: new Date('2025-01-14T09:00:00Z'),
    reviewedBy: 'manager',
    reviewedAt: new Date('2025-01-14T16:00:00Z')
  },
  {
    requestId: 'pr-004',
    datasetId: 'ds-003',
    datasetName: 'bronze_rpt_cds_lsp_perf',
    providerId: '75d99108-5fc6-449b-a9f9-d0716f2bbc29',
    providerName: 'starcatcher_origin_prod',
    tableName: 'bronze_rpt_cds_lsp_perf',
    tableVersion: 1,
    identityType: 'TEAM',
    identityValue: 'amzn1.abacus.team.tpxg2s2g4wrpihfjhvuq',
    businessJustificationType: 'Improve Amazon Services - Non-Pseudonymized data',
    businessJustification: 'AGL CN BI Team needs access for data quality monitoring and pipeline optimization.',
    status: 'approved',
    requestedBy: 'yyangzam',
    requestedAt: new Date('2025-01-13T11:00:00Z'),
    reviewedBy: 'admin',
    reviewedAt: new Date('2025-01-13T15:30:00Z')
  },

  // Pending requests
  {
    requestId: 'pr-005',
    datasetId: 'ds-004',
    datasetName: 'gold_logistics_metrics',
    providerId: '8a2b3c4d-5e6f-7890-abcd-ef1234567890',
    providerName: 'agl_analytics_prod',
    tableName: 'gold_logistics_metrics',
    tableVersion: 2,
    identityType: 'CRADLE_ACCOUNT',
    identityValue: 'aurora-weaver-cradle-dev',
    businessJustificationType: 'Provide Amazon services for repeatable interactions',
    businessJustification: 'Development environment access needed for testing new data pipeline features before production deployment.',
    status: 'pending',
    requestedBy: 'yyangzam',
    requestedAt: new Date('2025-01-28T08:00:00Z')
  },
  {
    requestId: 'pr-006',
    datasetId: 'ds-005',
    datasetName: 'bronze_raw_events',
    providerId: '9b3c4d5e-6f78-90ab-cdef-123456789012',
    providerName: 'agl_events_prod',
    tableName: 'bronze_raw_events',
    tableVersion: 1,
    identityType: 'DATANET_DBUSER',
    identityValue: 'terrasite_tier_1_prod',
    businessJustificationType: 'Provide other Amazon services',
    businessJustification: 'Terra Site requires access to raw event data for real-time monitoring dashboards.',
    status: 'pending',
    requestedBy: 'analyst1',
    requestedAt: new Date('2025-01-29T14:30:00Z')
  },
  {
    requestId: 'pr-007',
    datasetId: 'ds-006',
    datasetName: 'silver_inventory_snapshot',
    providerId: 'ab4c5d6e-7f89-0abc-def1-234567890123',
    providerName: 'agl_inventory_prod',
    tableName: 'silver_inventory_snapshot',
    tableVersion: 3,
    identityType: 'IAM_ROLE',
    identityValue: 'arn:aws:iam::123456789012:role/DataPipelineRole',
    businessJustificationType: 'Provide Amazon services for repeatable interactions',
    businessJustification: 'Automated data pipeline needs programmatic access for daily inventory reconciliation.',
    status: 'pending',
    requestedBy: 'pipeline-svc',
    requestedAt: new Date('2025-01-30T06:00:00Z')
  },

  // Rejected requests
  {
    requestId: 'pr-008',
    datasetId: 'ds-007',
    datasetName: 'gold_financial_summary',
    providerId: 'bc5d6e7f-8901-abcd-ef23-456789012345',
    providerName: 'agl_finance_prod',
    tableName: 'gold_financial_summary',
    tableVersion: 1,
    identityType: 'DATANET_DBUSER',
    identityValue: 'test_user_dev',
    businessJustificationType: 'Other',
    businessJustification: 'Need access for testing purposes.',
    status: 'rejected',
    requestedBy: 'testuser',
    requestedAt: new Date('2025-01-20T10:00:00Z'),
    reviewedBy: 'admin',
    reviewedAt: new Date('2025-01-21T09:00:00Z'),
    reviewNotes: 'Rejected: Insufficient business justification. Please provide specific use case and approval from data owner.'
  },
  {
    requestId: 'pr-009',
    datasetId: 'ds-008',
    datasetName: 'silver_customer_data',
    providerId: 'cd6e7f89-0123-bcde-f456-789012345678',
    providerName: 'agl_customer_prod',
    tableName: 'silver_customer_data',
    tableVersion: 2,
    identityType: 'TEAM',
    identityValue: 'amzn1.abacus.team.invalidteam123',
    businessJustificationType: 'Improve Amazon Services - Pseudonymized data',
    businessJustification: 'Customer analytics project.',
    status: 'rejected',
    requestedBy: 'analyst2',
    requestedAt: new Date('2025-01-22T15:00:00Z'),
    reviewedBy: 'manager',
    reviewedAt: new Date('2025-01-23T11:00:00Z'),
    reviewNotes: 'Rejected: Invalid team ID format. Please verify team identifier in Abacus.'
  },

  // More approved for variety
  {
    requestId: 'pr-010',
    datasetId: 'ds-002',
    datasetName: 'bronze_rpt_cds_carrier_perf',
    providerId: '75d99108-5fc6-449b-a9f9-d0716f2bbc29',
    providerName: 'starcatcher_origin_prod',
    tableName: 'bronze_rpt_cds_carrier_perf',
    tableVersion: 1,
    identityType: 'CRADLE_ACCOUNT',
    identityValue: 'aurora-weaver-cradle-prod',
    businessJustificationType: 'Provide Amazon services for repeatable interactions',
    businessJustification: 'Production Cradle access for shipment data processing pipeline.',
    status: 'approved',
    requestedBy: 'yyangzam',
    requestedAt: new Date('2025-01-10T08:00:00Z'),
    reviewedBy: 'admin',
    reviewedAt: new Date('2025-01-10T12:00:00Z')
  },
  {
    requestId: 'pr-011',
    datasetId: 'ds-003',
    datasetName: 'bronze_rpt_cds_lsp_perf',
    providerId: '75d99108-5fc6-449b-a9f9-d0716f2bbc29',
    providerName: 'starcatcher_origin_prod',
    tableName: 'bronze_rpt_cds_lsp_perf',
    tableVersion: 1,
    identityType: 'DATANET_DBUSER',
    identityValue: 'delta_site_tier_2_prod',
    businessJustificationType: 'Provide Amazon services for repeatable interactions',
    businessJustification: 'Delta Site analytics requires shipping request data for operational dashboards.',
    status: 'approved',
    requestedBy: 'yyangzam',
    requestedAt: new Date('2025-01-12T09:30:00Z'),
    reviewedBy: 'manager',
    reviewedAt: new Date('2025-01-12T14:00:00Z')
  },

  // Pending for different datasets
  {
    requestId: 'pr-012',
    datasetId: 'ds-001',
    datasetName: 'bronze_rpt_cds_retail_cga',
    providerId: '75d99108-5fc6-449b-a9f9-d0716f2bbc29',
    providerName: 'starcatcher_origin_prod',
    tableName: 'bronze_rpt_cds_retail_cga',
    tableVersion: 1,
    identityType: 'TEAM',
    identityValue: 'amzn1.abacus.team.newteam456xyz',
    businessJustificationType: 'Improve Amazon Services - Non-Pseudonymized data',
    businessJustification: 'New analytics team requires access for cross-functional reporting initiative.',
    status: 'pending',
    requestedBy: 'newanalyst',
    requestedAt: new Date('2025-01-30T10:00:00Z')
  }
];

export default mockPermissionRequests;
